import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'

// ─────────────────────────────────────────────────────────────────────────────
// READING THE MARKET — TIER 2 ADVANCED LESSON
// ─────────────────────────────────────────────────────────────────────────────
function SDCanvas({ width = 800, height = 260 }) {
  const cvs = useRef(null);
  useEffect(() => {
    const canvas = cvs.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, w, h);
    const pad = { l: 14, r: 56, t: 16, b: 28 };
    const cw = w - pad.l - pad.r, ch = h - pad.t - pad.b;
    const days = 60;
    const prices = [];
    let p = 168;
    const seed = (n => { let s = n; return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; }; })(42);
    for (let i = 0; i < days; i++) {
      const trend = i < 18 ? 0.15 : i < 32 ? 0.25 : i < 44 ? -0.35 : 0.2;
      p = Math.max(159, Math.min(190, p + (seed() - 0.48) * 1.6 + trend));
      prices.push(p);
    }
    const minP = 158, maxP = 191;
    const px = i => pad.l + (i / (days - 1)) * cw;
    const py = v => pad.t + (1 - (v - minP) / (maxP - minP)) * ch;
    // demand zone
    ctx.fillStyle = 'rgba(122,171,138,0.13)';
    ctx.fillRect(pad.l, py(166), cw, py(162) - py(166));
    ctx.strokeStyle = 'rgba(122,171,138,0.4)'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(pad.l, py(164)); ctx.lineTo(pad.l + cw, py(164)); ctx.stroke();
    ctx.setLineDash([]);
    // supply zone
    ctx.fillStyle = 'rgba(196,122,122,0.13)';
    ctx.fillRect(pad.l, py(186), cw, py(183) - py(186));
    ctx.strokeStyle = 'rgba(196,122,122,0.4)'; ctx.lineWidth = 1; ctx.setLineDash([4, 3]);
    ctx.beginPath(); ctx.moveTo(pad.l, py(184.5)); ctx.lineTo(pad.l + cw, py(184.5)); ctx.stroke();
    ctx.setLineDash([]);
    // grid
    [162, 168, 174, 180, 186].forEach(v => {
      ctx.fillStyle = 'rgba(240,237,230,0.25)'; ctx.font = '9px Jost, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText('$' + v, w - pad.r + 4, py(v) + 3);
      ctx.strokeStyle = 'rgba(240,237,230,0.04)'; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(pad.l, py(v)); ctx.lineTo(pad.l + cw, py(v)); ctx.stroke();
    });
    // price line
    ctx.beginPath(); ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 2; ctx.lineJoin = 'round';
    prices.forEach((v, i) => i === 0 ? ctx.moveTo(px(i), py(v)) : ctx.lineTo(px(i), py(v)));
    ctx.stroke();
    // labels
    ctx.font = '10px Jost, sans-serif'; ctx.textAlign = 'center';
    ctx.fillStyle = 'rgba(122,171,138,0.85)';
    ctx.fillText('DEMAND ZONE $162–$166', pad.l + cw * 0.12, py(164) - 5);
    ctx.fillStyle = 'rgba(196,122,122,0.85)';
    ctx.fillText('SUPPLY ZONE $183–$186', pad.l + cw * 0.85, py(184.5) - 5);
  }, []);
  return <canvas ref={cvs} width={width} height={height} style={{ width: '100%', height: `${height}px`, borderRadius: '8px', display: 'block' }} />;
}

function OICanvas({ width = 800, height = 280 }) {
  const cvs = useRef(null);
  useEffect(() => {
    const canvas = cvs.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, w, h);
    const strikes = [155,160,165,170,175,180,185,190];
    const putOI   = [420, 890, 3200, 1800, 980, 540, 210, 80];
    const callOI  = [60, 150, 480, 1200, 2100, 3800, 1600, 520];
    const maxOI = 4000;
    const rowH = (h - 40) / strikes.length;
    const midX = w / 2;
    const barMax = midX - 80;
    ctx.font = '10px Jost, sans-serif';
    strikes.forEach((s, i) => {
      const y = 24 + i * rowH;
      const py = s === 165 ? 'rgba(122,171,138,0.25)' : s === 185 ? 'rgba(196,122,122,0.2)' : 'transparent';
      ctx.fillStyle = py; ctx.fillRect(0, y - 2, w, rowH - 2);
      // put bar (left)
      const pw = (putOI[i] / maxOI) * barMax;
      ctx.fillStyle = s === 165 ? 'rgba(122,171,138,0.7)' : 'rgba(122,171,138,0.35)';
      ctx.fillRect(midX - 40 - pw, y + 2, pw, rowH - 8);
      // call bar (right)
      const cw2 = (callOI[i] / maxOI) * barMax;
      ctx.fillStyle = s === 185 ? 'rgba(196,122,122,0.7)' : 'rgba(196,122,122,0.35)';
      ctx.fillRect(midX + 40, y + 2, cw2, rowH - 8);
      // strike label
      ctx.fillStyle = (s === 165 || s === 185) ? '#c9a84c' : 'rgba(240,237,230,0.6)';
      ctx.textAlign = 'center'; ctx.fillText('$' + s, midX, y + rowH / 2 + 1);
      // OI numbers
      ctx.fillStyle = 'rgba(240,237,230,0.35)'; ctx.textAlign = 'right';
      ctx.fillText(putOI[i].toLocaleString(), midX - 44, y + rowH / 2 + 1);
      ctx.textAlign = 'left';
      ctx.fillText(callOI[i].toLocaleString(), midX + 44, y + rowH / 2 + 1);
    });
    ctx.fillStyle = 'rgba(122,171,138,0.7)'; ctx.textAlign = 'right'; ctx.font = '9px Jost, sans-serif';
    ctx.fillText('← PUTS', midX - 45, 14);
    ctx.fillStyle = 'rgba(196,122,122,0.7)'; ctx.textAlign = 'left';
    ctx.fillText('CALLS →', midX + 45, 14);
    ctx.fillStyle = 'rgba(201,168,76,0.6)'; ctx.textAlign = 'center';
    ctx.fillText('STRIKE', midX, 14);
    // gold markers
    ctx.strokeStyle = 'rgba(201,168,76,0.6)'; ctx.lineWidth = 1.5; ctx.setLineDash([5,3]);
    const y165 = 24 + 2 * rowH + rowH / 2;
    const y185 = 24 + 6 * rowH + rowH / 2;
    ctx.beginPath(); ctx.moveTo(0, y165); ctx.lineTo(w, y165); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, y185); ctx.lineTo(w, y185); ctx.stroke();
    ctx.setLineDash([]);
  }, []);
  return <canvas ref={cvs} width={width} height={height} style={{ width: '100%', height: `${height}px`, borderRadius: '8px', display: 'block' }} />;
}

function MomentumCanvas({ width = 800, height = 380 }) {
  const cvs = useRef(null);
  useEffect(() => {
    const canvas = cvs.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, w, h);
    const days = 60;
    const seed = (n => { let s = n; return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; }; })(77);
    const prices = []; let p = 170;
    for (let i = 0; i < days; i++) {
      const t = i < 20 ? 0.18 : i < 38 ? 0.08 : i < 50 ? -0.22 : 0.15;
      p = Math.max(155, Math.min(195, p + (seed() - 0.47) * 1.8 + t));
      prices.push(p);
    }
    const ma5  = prices.map((_, i) => i < 4  ? null : prices.slice(i-4,  i+1).reduce((a,b)=>a+b)/5);
    const ma20 = prices.map((_, i) => i < 19 ? null : prices.slice(i-19, i+1).reduce((a,b)=>a+b)/20);
    const ma50 = prices.map((_, i) => i < 49 ? null : prices.slice(i-49, i+1).reduce((a,b)=>a+b)/50);
    const rsi = prices.map((_, i) => {
      if (i < 14) return 50;
      const diffs = prices.slice(i-13, i+1).map((v,j,a) => j===0?0:v-a[j-1]);
      const gains = diffs.filter(d=>d>0).reduce((a,b)=>a+b,0)/14;
      const losses = diffs.filter(d=>d<0).map(d=>-d).reduce((a,b)=>a+b,0)/14;
      return losses === 0 ? 100 : 100 - 100/(1+gains/losses);
    });
    const priceH = h * 0.55, rsiH = h * 0.25, volH = h * 0.2;
    const minP = Math.min(...prices) - 3, maxP = Math.max(...prices) + 3;
    const px = i => 14 + (i / (days-1)) * (w - 70);
    const ppy = v => 8 + (1-(v-minP)/(maxP-minP)) * (priceH - 16);
    const rpy = v => priceH + 4 + (1-v/100)*(rsiH - 8);
    const vpy = v => priceH + rsiH + 4 + (1-v)*(volH - 8);
    // price panel bg
    ctx.fillStyle = 'rgba(240,237,230,0.02)'; ctx.fillRect(0, 0, w, priceH);
    // price line
    ctx.beginPath(); ctx.strokeStyle = '#c9a84c'; ctx.lineWidth = 1.5;
    prices.forEach((v,i) => i===0?ctx.moveTo(px(i),ppy(v)):ctx.lineTo(px(i),ppy(v)));
    ctx.stroke();
    // MAs
    const drawMA = (data, col) => {
      ctx.beginPath(); ctx.strokeStyle = col; ctx.lineWidth = 1.2; let first = true;
      data.forEach((v,i)=>{ if(!v)return; first?(ctx.moveTo(px(i),ppy(v)),first=false):ctx.lineTo(px(i),ppy(v)); });
      ctx.stroke();
    };
    drawMA(ma5, '#7a9ec4'); drawMA(ma20, '#7aab8a'); drawMA(ma50, '#c47a7a');
    // MA legend
    [['5 MA','#7a9ec4'],['20 MA','#7aab8a'],['50 MA','#c47a7a']].forEach(([l,c],i) => {
      ctx.fillStyle = c; ctx.fillRect(w-62+i*0, 8+i*13, 8, 8);
      ctx.fillStyle = c; ctx.font = '9px Jost,sans-serif'; ctx.textAlign = 'left'; ctx.fillText(l, w-52, 15+i*13);
    });
    // RSI panel
    ctx.fillStyle = 'rgba(240,237,230,0.015)'; ctx.fillRect(0, priceH, w, rsiH);
    [70, 50, 30].forEach(v => {
      ctx.strokeStyle = v===50?'rgba(240,237,230,0.06)':'rgba(154,122,196,0.25)'; ctx.lineWidth=0.5; ctx.setLineDash(v===50?[]:[3,3]);
      ctx.beginPath(); ctx.moveTo(14,rpy(v)); ctx.lineTo(w-56,rpy(v)); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle='rgba(154,122,196,0.5)'; ctx.font='8px Jost,sans-serif'; ctx.textAlign='left';
      ctx.fillText(v,w-52,rpy(v)+3);
    });
    ctx.beginPath(); ctx.strokeStyle='#9a7ac4'; ctx.lineWidth=1.5;
    rsi.forEach((v,i)=>i===0?ctx.moveTo(px(i),rpy(v)):ctx.lineTo(px(i),rpy(v))); ctx.stroke();
    ctx.fillStyle='rgba(154,122,196,0.5)'; ctx.font='8px Jost,sans-serif'; ctx.textAlign='right';
    ctx.fillText('RSI',w-56,priceH+10);
    // Volume panel
    ctx.fillStyle='rgba(240,237,230,0.01)'; ctx.fillRect(0,priceH+rsiH,w,volH);
    prices.forEach((v,i)=>{ if(i===0)return;
      const bull=v>=prices[i-1];
      const vol=0.3+(seed()-0.1)*0.7;
      ctx.fillStyle=bull?'rgba(122,171,138,0.45)':'rgba(196,122,122,0.45)';
      const bw=Math.max(1,(w-70)/days*0.7);
      ctx.fillRect(px(i)-bw/2, vpy(vol), bw, priceH+rsiH+volH - vpy(vol) - 4);
    });
    // dividers
    ctx.strokeStyle='rgba(201,168,76,0.1)'; ctx.lineWidth=0.5; ctx.setLineDash([]);
    [priceH, priceH+rsiH].forEach(y=>{ ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(w,y); ctx.stroke(); });
    // annotations
    ctx.fillStyle='rgba(122,171,138,0.7)'; ctx.font='9px Jost,sans-serif'; ctx.textAlign='center';
    ctx.fillText('Golden Cross', px(22), ppy(ma20[22]||176)-8);
    ctx.fillStyle='rgba(196,122,122,0.7)';
    ctx.fillText('Bearish Divergence', px(38), ppy(prices[38])+14);
    ctx.fillStyle='rgba(201,168,76,0.7)';
    ctx.fillText('Vol Confirms', px(50), priceH+rsiH+8);
  }, []);
  return <canvas ref={cvs} width={width} height={height} style={{ width: '100%', height: `${height}px`, borderRadius: '8px', display: 'block' }} />;
}

function AllTogetherCanvas({ phase }) {
  const cvs = useRef(null);
  useEffect(() => {
    const canvas = cvs.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    ctx.fillStyle = '#0a0a0c'; ctx.fillRect(0, 0, w, h);
    const days = 60;
    const seed = (n => { let s = n; return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; }; })(99);
    const allPrices = [];
    let p = 163;
    const targets = [163,163,165,167,169,171,173,175,176,177,178,179,180,181,182,183,184,185,185,185,185,184,183,182,181,180,179,178,177,176,175,174,173,172,171,170,169,168,167,166,165,165,165,165,164,164,165,166,167,168,169,170,171,172,173,174,175,174,175,174];
    for (let i = 0; i < days; i++) {
      p = targets[i] + (seed()-0.5)*0.8;
      allPrices.push(Math.max(160,Math.min(190,p)));
    }
    const showDays = phase <= 0 ? 20 : phase === 1 ? 35 : phase === 2 ? 42 : phase === 3 ? 52 : 60;
    const prices = allPrices.slice(0, showDays);
    const priceH = h * 0.60, rsiH = h * 0.22, volH = h * 0.18;
    const minP = 158, maxP = 191;
    const px = i => 14 + (i / (days-1)) * (w - 70);
    const ppy = v => 8 + (1-(v-minP)/(maxP-minP)) * (priceH - 16);
    const rpy = v => priceH + 4 + (1-v/100)*(rsiH - 8);
    const rsi = allPrices.map((_, i) => {
      if (i < 14) return 55;
      const diffs = allPrices.slice(i-13,i+1).map((v,j,a)=>j===0?0:v-a[j-1]);
      const g = diffs.filter(d=>d>0).reduce((a,b)=>a+b,0)/14;
      const l = diffs.filter(d=>d<0).map(d=>-d).reduce((a,b)=>a+b,0)/14;
      return l===0?100:100-100/(1+g/l);
    });
    // demand zone
    ctx.fillStyle='rgba(122,171,138,0.12)'; ctx.fillRect(14,ppy(166),w-70,ppy(162)-ppy(166));
    ctx.strokeStyle='rgba(122,171,138,0.4)'; ctx.lineWidth=1; ctx.setLineDash([4,3]);
    ctx.beginPath(); ctx.moveTo(14,ppy(164)); ctx.lineTo(w-56,ppy(164)); ctx.stroke();
    // supply zone
    ctx.fillStyle='rgba(196,122,122,0.12)'; ctx.fillRect(14,ppy(186),w-70,ppy(183)-ppy(186));
    ctx.strokeStyle='rgba(196,122,122,0.4)';
    ctx.beginPath(); ctx.moveTo(14,ppy(184.5)); ctx.lineTo(w-56,ppy(184.5)); ctx.stroke();
    ctx.setLineDash([]);
    // OI lines
    [164, 184.5].forEach((v,i) => {
      ctx.strokeStyle='rgba(201,168,76,0.5)'; ctx.lineWidth=1.5; ctx.setLineDash([6,4]);
      ctx.beginPath(); ctx.moveTo(14,ppy(v)); ctx.lineTo(w-56,ppy(v)); ctx.stroke(); ctx.setLineDash([]);
    });
    // price labels
    [162,168,174,180,186].forEach(v => {
      ctx.fillStyle='rgba(240,237,230,0.2)'; ctx.font='8px Jost,sans-serif'; ctx.textAlign='left';
      ctx.fillText('$'+v,w-52,ppy(v)+3);
      ctx.strokeStyle='rgba(240,237,230,0.04)'; ctx.lineWidth=0.5;
      ctx.beginPath(); ctx.moveTo(14,ppy(v)); ctx.lineTo(w-56,ppy(v)); ctx.stroke();
    });
    // price
    ctx.beginPath(); ctx.strokeStyle='#c9a84c'; ctx.lineWidth=2; ctx.lineJoin='round';
    prices.forEach((v,i)=>i===0?ctx.moveTo(px(i),ppy(v)):ctx.lineTo(px(i),ppy(v))); ctx.stroke();
    // end dot
    if (prices.length > 0) {
      ctx.fillStyle='#c9a84c';
      ctx.beginPath(); ctx.arc(px(prices.length-1),ppy(prices[prices.length-1]),4,0,Math.PI*2); ctx.fill();
    }
    // RSI panel
    ctx.fillStyle='rgba(240,237,230,0.015)'; ctx.fillRect(0,priceH,w,rsiH);
    [70,50,30].forEach(v=>{
      ctx.strokeStyle=v===50?'rgba(240,237,230,0.05)':'rgba(154,122,196,0.2)'; ctx.lineWidth=0.5; ctx.setLineDash(v===50?[]:[3,3]);
      ctx.beginPath(); ctx.moveTo(14,rpy(v)); ctx.lineTo(w-56,rpy(v)); ctx.stroke(); ctx.setLineDash([]);
      ctx.fillStyle='rgba(154,122,196,0.45)'; ctx.font='7px Jost,sans-serif'; ctx.textAlign='left'; ctx.fillText(v,w-50,rpy(v)+3);
    });
    ctx.beginPath(); ctx.strokeStyle='#9a7ac4'; ctx.lineWidth=1.5;
    rsi.slice(0,showDays).forEach((v,i)=>i===0?ctx.moveTo(px(i),rpy(v)):ctx.lineTo(px(i),rpy(v))); ctx.stroke();
    // Volume
    ctx.fillStyle='rgba(240,237,230,0.01)'; ctx.fillRect(0,priceH+rsiH,w,volH);
    const vBase = priceH+rsiH+volH-4;
    prices.forEach((v,i)=>{ if(i===0)return;
      const bull=v>=prices[i-1], vol=0.3+(seed()-0.1)*0.55;
      ctx.fillStyle=bull?'rgba(122,171,138,0.4)':'rgba(196,122,122,0.4)';
      const bw=Math.max(1,(w-70)/days*0.65);
      ctx.fillRect(px(i)-bw/2,vBase-vol*(volH-8),bw,vol*(volH-8));
    });
    // dividers
    ctx.strokeStyle='rgba(201,168,76,0.08)'; ctx.lineWidth=0.5; ctx.setLineDash([]);
    [priceH,priceH+rsiH].forEach(y=>{ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(w,y);ctx.stroke();});
    // zone labels
    ctx.font='9px Jost,sans-serif'; ctx.textAlign='left';
    ctx.fillStyle='rgba(122,171,138,0.7)'; ctx.fillText('DEMAND $162–$166',16,ppy(164)-5);
    ctx.fillStyle='rgba(196,122,122,0.7)'; ctx.fillText('SUPPLY $183–$186',16,ppy(186)-5);
    ctx.fillStyle='rgba(201,168,76,0.6)'; ctx.fillText('High Put OI',w-56,ppy(164)+12);
    ctx.fillStyle='rgba(201,168,76,0.6)'; ctx.fillText('High Call OI',w-56,ppy(184.5)+12);
    ctx.fillStyle='rgba(154,122,196,0.5)'; ctx.textAlign='right'; ctx.fillText('RSI',w-56,priceH+12);
  }, [phase]);
  return <canvas ref={cvs} width={800} height={440} style={{ width:'100%', height:'440px', borderRadius:'8px', display:'block' }} />;
}

const ATQ = [
  { phase: 0, question: 'You are considering selling a covered call. What does the current setup suggest?',
    options: ['Sell the $175 call — close to current price','Sell the $185 call — at the supply and OI resistance','Don\'t sell — trend is too strong','Sell the $190 call — above resistance'],
    correct: 1, explanation: 'The supply zone at $183–$186 combined with high call OI at $185 makes this an ideal covered call strike. If price stalls here — which supply and OI suggest it might — the call expires worthless.' },
  { phase: 1, question: 'RSI is diverging from price. Volume is declining. Price is approaching the $185 supply zone. What do these three signals together suggest?',
    options: ['Strong momentum — add to positions','Mixed signals — reduce size and tighten rules','Clear reversal confirmed — exit all positions','No signal yet — hold and wait'],
    correct: 1, explanation: 'No single indicator confirms a reversal yet — but all three are showing caution simultaneously. This is when professional traders reduce exposure and tighten their rules, not when they add risk.' },
  { phase: 2, question: 'Price rejected sharply at the $185 supply zone on high volume. RSI is turning down. What is the most likely near-term scenario?',
    options: ['Price will immediately crash — sell puts','Price may pull back toward $165 demand and OI support — watch for bounce entry','The uptrend is over permanently','Buy the $185 calls — it will break through'],
    correct: 1, explanation: 'The $165 level has strong demand zone history AND high put OI — double support. A pullback to this level with RSI reaching oversold would be a high-quality CSP entry setup. Patient traders wait for price to come to them.' },
  { phase: 3, question: 'Price has bounced from the $165 demand zone. RSI is at 32 and turning up. Volume is increasing on the bounce. What does this setup suggest?',
    options: ['Sell a CSP at the $165 strike — demand and OI support confirmed, RSI turning from oversold, volume confirming','Wait for more confirmation','The downtrend is beginning — avoid','Buy calls for the move higher'],
    correct: 0, explanation: 'This is a textbook CSP setup. Supply and demand shows strong buyer support at $165. High put OI confirms institutional positioning at this level. RSI turning from oversold shows momentum shifting bullish. Volume confirms buyer conviction on the bounce. Three independent tools pointing to the same conclusion.' },
];

const PHASE_LABELS = [
  { day: '1–20', title: 'Phase 1: The Setup', note: 'Strong trend confirmed. All indicators aligned bullish. Supply zone and high call OI at $185 ahead.' },
  { day: '21–35', title: 'Phase 2: Warning Signs', note: 'Price approaching supply. RSI showing bearish divergence. Volume declining. Three caution signals simultaneously.' },
  { day: '36–42', title: 'Phase 3: The Rejection', note: 'Supply zone and OI resistance held. Volume spike confirms seller conviction. RSI confirms momentum shift.' },
  { day: '43–60', title: 'Phase 4–5: Pullback & Bounce', note: 'Price retraces to $165 demand and OI support. RSI oversold and turning up. Volume confirming the bounce.' },
];

const ADV_QUIZ = [
  { q: 'A stock is approaching a level with very high put open interest from below. What does this suggest?', options: ['It will definitely hold as support','It is likely to act as support but not guaranteed','It means the stock will fall through','OI has no relationship to price levels'], correct: 1 },
  { q: 'Price makes a new high but RSI makes a lower high. This is called:', options: ['A golden cross','Bullish divergence','Bearish divergence','A death cross'], correct: 2 },
  { q: 'A price breakout occurs on very low volume. What does this suggest?', options: ['Strong momentum — add to position','The breakout lacks conviction and may be a false move','Volume doesn\'t matter for breakouts','Always trade breakouts regardless of volume'], correct: 1 },
  { q: 'The 20 day MA crosses below the 50 day MA. This is called a:', options: ['Golden cross','Bull flag','Death cross','Bearish divergence'], correct: 2 },
  { q: 'You see price at a demand zone, RSI at 28 and turning up, and volume increasing. Taken together these suggest:', options: ['The downtrend is accelerating','A potential bounce setup with multiple confirmations','RSI and volume contradict each other','Wait for all indicators to reach extreme levels'], correct: 1 },
];

function ReadingTheMarketView({ state, onBack, onNavigate }) {
  const [tab, setTab] = useState(0);
  const [atPhase, setAtPhase] = useState(-1);
  const [atAnswer, setAtAnswer] = useState(null);
  const [atAnswered, setAtAnswered] = useState([]);
  const [atDone, setAtDone] = useState(false);
  const [quizQ, setQuizQ] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizFeed, setQuizFeed] = useState(false);
  const [quizScore, setQuizScore] = useState(null);
  const serif = { fontFamily:"'Cormorant Garamond', serif" };
  const body = { fontSize:'14px', color:'rgba(240,237,230,0.85)', fontFamily:"'Jost',sans-serif", lineHeight:1.9, margin:'0 0 16px' };
  const secTitle = { ...serif, fontSize:'22px', fontWeight:400, color:V.gold, margin:'0 0 14px' };
  const divider = { height:'0.5px', background:'rgba(201,168,76,0.1)', margin:'28px 0' };
  const quote = (text, attr) => (
    <div style={{ borderLeft:`3px solid ${V.gold}`, paddingLeft:'18px', margin:'20px 0' }}>
      <p style={{ ...serif, fontSize:'16px', fontStyle:'italic', color:V.goldL, lineHeight:1.75, margin:'0 0 8px' }}>"{text}"</p>
      <span style={{ fontSize:'12px', color:V.muted, fontFamily:"'Jost',sans-serif" }}>— {attr}</span>
    </div>
  );
  const sectionBlock = (title, children) => (
    <div style={{ marginBottom:'28px' }}>
      <h3 style={secTitle}>{title}</h3>
      {children}
    </div>
  );
  const infoBox = (text, color = 'rgba(201,168,76,0.08)', border = V.border) => (
    <div style={{ background:color, border:`0.5px solid ${border}`, borderRadius:'8px', padding:'14px 18px', marginBottom:'16px' }}>
      <p style={{ fontSize:'13px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.8, margin:0, whiteSpace:'pre-line' }}>{text}</p>
    </div>
  );

  const handleATNext = () => {
    if (atPhase < 3) { setAtPhase(p => p + 1); setAtAnswer(null); }
    else { setAtDone(true); }
  };
  const pickATAnswer = (i) => { if (atAnswer !== null) return; setAtAnswer(i); setAtAnswered(prev => [...prev, i]); };

  const pickQuizAnswer = (i) => {
    if (quizFeed) return;
    setQuizAnswers(prev => ({ ...prev, [quizQ]: i }));
    setQuizFeed(true);
    setTimeout(() => {
      setQuizFeed(false);
      if (quizQ < ADV_QUIZ.length - 1) { setQuizQ(q => q + 1); }
      else {
        const score = ADV_QUIZ.reduce((acc, q, qi) => acc + (quizAnswers[qi] === q.correct || (qi === ADV_QUIZ.length - 1 && i === q.correct) ? 1 : 0), 0);
        setQuizScore(Math.round((score / ADV_QUIZ.length) * 100));
        try { localStorage.setItem('tlaw_adv_reading_quiz', JSON.stringify({ score, date: new Date().toISOString() })); } catch(e) {}
      }
    }, 1800);
  };

  if (!state.tier2Unlocked) {
    return (
      <div style={{ padding:'40px 24px', display:'flex', justifyContent:'center' }}>
        <div style={{ background:V.bg2, border:`1px solid ${V.border}`, borderRadius:'14px', padding:'48px 40px', maxWidth:'600px', width:'100%', textAlign:'center' }}>
          <div style={{ fontSize:'32px', color:V.gold, marginBottom:'20px' }}>🔒</div>
          <h2 style={{ ...serif, fontSize:'28px', fontWeight:400, color:V.gold, margin:'0 0 12px' }}>Reading the Market</h2>
          <p style={{ fontSize:'14px', color:V.muted, fontFamily:"'Jost',sans-serif", marginBottom:'20px', lineHeight:1.75 }}>This advanced lesson unlocks when your mentor approves your Tier 2 access.</p>
          <p style={{ fontSize:'13px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.8, marginBottom:'24px', maxWidth:'440px', margin:'0 auto 24px' }}>
            You will learn to read supply and demand zones, open interest levels, and price momentum signals — and combine all three into a complete market read before every trade.
          </p>
          <p style={{ fontSize:'12px', color:V.dim, fontFamily:"'Jost',sans-serif", fontStyle:'italic', lineHeight:1.75, marginBottom:'28px' }}>
            Keep logging your paper trades.<br/>Keep following your risk rules.<br/>Your mentor is watching your progress.
          </p>
          <button onClick={onBack} style={{ padding:'10px 22px', background:'transparent', border:`0.5px solid ${V.border}`, borderRadius:'8px', color:V.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}>← Back</button>
        </div>
      </div>
    );
  }

  const TABS = ['Supply & Demand', 'Open Interest', 'Momentum', 'All Together'];

  const renderTab0 = () => (
    <div>
      <h2 style={{ ...serif, fontSize:'28px', fontWeight:400, color:V.gold, margin:'0 0 4px' }}>Supply & Demand</h2>
      {quote('In the short run the market is a voting machine. In the long run it is a weighing machine.', 'Benjamin Graham')}
      {sectionBlock('Why prices move',
        <p style={body}>Every price move in stocks and options is driven by the same force that drives every market in history: supply and demand. When more buyers want a stock than sellers are willing to supply — price rises until enough sellers are attracted. When more sellers want to exit than buyers are willing to absorb — price falls until enough buyers are attracted. This is not theory. This is every tick on every chart you will ever read.</p>
      )}
      {sectionBlock('Supply and demand zones',
        <>
          <p style={body}>Traders who study supply and demand look for zones — price areas where significant buying or selling occurred in the past and is likely to occur again.</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'20px' }}>
            <div style={{ background:'rgba(122,171,138,0.07)', border:'0.5px solid rgba(122,171,138,0.25)', borderRadius:'10px', padding:'16px 18px' }}>
              <div style={{ fontSize:'11px', letterSpacing:'0.12em', color:V.sage, textTransform:'uppercase', fontFamily:"'Jost',sans-serif", marginBottom:'8px' }}>Demand Zones (Support)</div>
              <p style={{ ...body, margin:0 }}>Areas where buyers previously stepped in strongly. Price left quickly. The move away was significant. Zone weakens with each revisit. High volume when created.</p>
            </div>
            <div style={{ background:'rgba(196,122,122,0.07)', border:'0.5px solid rgba(196,122,122,0.25)', borderRadius:'10px', padding:'16px 18px' }}>
              <div style={{ fontSize:'11px', letterSpacing:'0.12em', color:V.rose, textTransform:'uppercase', fontFamily:"'Jost',sans-serif", marginBottom:'8px' }}>Supply Zones (Resistance)</div>
              <p style={{ ...body, margin:0 }}>Areas where sellers previously overwhelmed buyers. Price left quickly and strongly. First or second test is strongest. High volume when created.</p>
            </div>
          </div>
          <SDCanvas />
          <p style={{ fontSize:'12px', color:V.muted, fontFamily:"'Jost',sans-serif", marginTop:'8px', lineHeight:1.6, fontStyle:'italic' }}>Sage shaded area = demand zone. Rose shaded area = supply zone. Gold line = price. Notice how price bounces from demand and rejects at supply.</p>
        </>
      )}
      {sectionBlock('How this applies to options',
        <>
          <p style={body}>Supply and demand zones matter for options sellers in two specific ways:</p>
          {[
            ['Strike selection', 'Selling puts at or above strong demand zones increases your probability. The market is telling you buyers have defended this level.'],
            ['Premium pricing', 'When price approaches a supply zone implied volatility often rises as uncertainty increases. This means richer premium for sellers who position correctly.'],
          ].map(([l,d]) => (
            <div key={l} style={{ display:'flex', gap:'10px', marginBottom:'12px' }}>
              <span style={{ color:V.gold, fontSize:'14px', flexShrink:0, lineHeight:'1.85' }}>→</span>
              <div><span style={{ fontSize:'13px', color:V.text, fontFamily:"'Jost',sans-serif", fontWeight:500 }}>{l} — </span><span style={{ fontSize:'13px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.75 }}>{d}</span></div>
            </div>
          ))}
          {infoBox("tastylive research confirms that selling puts near identified support levels improves win rates compared to selling at arbitrary delta targets alone.", 'rgba(201,168,76,0.05)', 'rgba(201,168,76,0.2)')}
        </>
      )}
    </div>
  );

  const renderTab1 = () => (
    <div>
      <h2 style={{ ...serif, fontSize:'28px', fontWeight:400, color:V.gold, margin:'0 0 4px' }}>Open Interest</h2>
      {quote('Follow the money. Open interest shows you where it has gone.', 'Options trading wisdom')}
      {sectionBlock('What is open interest',
        <>
          <p style={body}>Open interest is the total number of outstanding options contracts that have not been closed or expired. Volume tells you how many contracts traded today. Open interest tells you how many contracts are currently open — how many people have skin in the game at each strike.</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px' }}>
            {[
              ['High vol + rising OI', 'New positions opening. Real conviction.', V.sage],
              ['High vol + falling OI', 'Positions closing. The move may be ending.', V.rose],
              ['Low vol + rising OI', 'Positions building quietly. Watch this level.', V.gold],
              ['Low vol + falling OI', 'Market losing interest in this strike.', V.muted],
            ].map(([l,d,c]) => (
              <div key={l} style={{ background:V.bg3, border:`0.5px solid ${V.border}`, borderRadius:'8px', padding:'12px 14px' }}>
                <div style={{ fontSize:'12px', fontWeight:500, color:c, fontFamily:"'Jost',sans-serif", marginBottom:'4px' }}>{l}</div>
                <div style={{ fontSize:'12px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.65 }}>{d}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {sectionBlock('OI as price magnets',
        <>
          <p style={body}>High open interest strikes often become price magnets — especially near expiration. Market makers who sold those options need to hedge their exposure. As price approaches a high OI strike their hedging activity creates buying or selling pressure that can push price toward — or pin it at — that level.</p>
          <p style={body}>This is sometimes called max pain theory: the price at expiration where the maximum number of options contracts expire worthless — causing the maximum pain to options buyers. Knowing the max pain level for the current expiration gives you one more data point when selecting strikes.</p>
        </>
      )}
      {sectionBlock('OI distribution chart — SPY example',
        <>
          <OICanvas />
          <p style={{ fontSize:'12px', color:V.muted, fontFamily:"'Jost',sans-serif", marginTop:'8px', lineHeight:1.6, fontStyle:'italic' }}>Left bars = put OI. Right bars = call OI. Gold highlighted strikes ($165 put, $185 call) show the highest open interest — these levels tend to attract price near expiration.</p>
        </>
      )}
      {sectionBlock('Reading OI for strike selection',
        <>
          <p style={body}>Before selecting a strike look at the open interest distribution across the options chain. Ask these questions:</p>
          {['Where is the highest put OI? This level has the most buyers betting the stock falls here — and the most sellers who collected premium. Large put OI can act as support.',
            'Where is the highest call OI? This level has the most buyers betting the stock rises here. Large call OI can act as resistance.',
            'Is OI building or declining at your target strike? Building OI = increasing interest. Declining = positions closing.',
          ].map((t,i) => (
            <div key={i} style={{ display:'flex', gap:'10px', marginBottom:'12px' }}>
              <span style={{ color:V.gold, fontSize:'14px', flexShrink:0, lineHeight:'1.85' }}>→</span>
              <p style={{ ...body, margin:0 }}>{t}</p>
            </div>
          ))}
        </>
      )}
      {sectionBlock('OI and liquidity',
        <>{infoBox('Low OI = wide bid-ask spreads = expensive to trade = avoid if possible.\n\nHigh OI = tight bid-ask spreads = efficient pricing = prefer these strikes.\n\nAlways check that your target strike has sufficient OI before entering. For SPX options most strikes have excellent liquidity. For individual stocks check carefully — illiquid options can cost you significantly on entry and exit.')}</>
      )}
    </div>
  );

  const renderTab2 = () => (
    <div>
      <h2 style={{ ...serif, fontSize:'28px', fontWeight:400, color:V.gold, margin:'0 0 4px' }}>Price Momentum</h2>
      {quote('The trend is your friend until it ends. Momentum tells you when it is ending.', 'Ed Seykota, adapted')}
      {sectionBlock('What is momentum',
        <><p style={body}>Momentum measures the rate of price change — not just direction but conviction. A stock can be going up slowly with fading momentum. A stock can be going down quickly with powerful momentum. Direction tells you where. Momentum tells you how strongly and for how long.</p>
        <p style={body}>For options sellers momentum matters because strong bullish momentum confirms a good environment for selling puts, while weakening momentum near resistance signals a covered call opportunity. Bearish momentum accelerating means reduce size, widen strikes, or step aside.</p></>
      )}
      {sectionBlock('RSI — deeper dive',
        <>
          <p style={body}>You learned RSI basics in Tier 1. Here we go deeper into divergence — the most powerful RSI signal most beginners miss because they are only looking at price, not at whether momentum is confirming it.</p>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div style={{ background:'rgba(122,171,138,0.07)', border:'0.5px solid rgba(122,171,138,0.2)', borderRadius:'9px', padding:'14px 16px' }}>
              <div style={{ fontSize:'11px', color:V.sage, letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'Jost',sans-serif", marginBottom:'6px' }}>Bullish Divergence</div>
              <p style={{ ...body, margin:0, fontSize:'13px' }}>Price makes a lower low but RSI makes a higher low. Selling pressure is weakening even as price falls. Often precedes a reversal upward.</p>
            </div>
            <div style={{ background:'rgba(196,122,122,0.07)', border:'0.5px solid rgba(196,122,122,0.2)', borderRadius:'9px', padding:'14px 16px' }}>
              <div style={{ fontSize:'11px', color:V.rose, letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'Jost',sans-serif", marginBottom:'6px' }}>Bearish Divergence</div>
              <p style={{ ...body, margin:0, fontSize:'13px' }}>Price makes a higher high but RSI makes a lower high. Buying pressure is weakening even as price rises. Often precedes a reversal downward.</p>
            </div>
          </div>
          {infoBox('In a strong uptrend RSI tends to stay between 40–80. Readings below 40 are buying opportunities in uptrends.\nIn a strong downtrend RSI tends to stay between 20–60. Readings above 60 are selling opportunities in downtrends.')}
        </>
      )}
      {sectionBlock('Moving average crossovers',
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
            <div style={{ background:'rgba(122,171,138,0.07)', border:'0.5px solid rgba(122,171,138,0.2)', borderRadius:'9px', padding:'14px 16px' }}>
              <div style={{ fontSize:'13px', fontWeight:500, color:V.sage, fontFamily:"'Jost',sans-serif", marginBottom:'6px' }}>Golden Cross</div>
              <p style={{ ...body, margin:0, fontSize:'13px' }}>Faster MA crosses above slower MA. Signals bullish momentum building. Good environment for selling puts.</p>
            </div>
            <div style={{ background:'rgba(196,122,122,0.07)', border:'0.5px solid rgba(196,122,122,0.2)', borderRadius:'9px', padding:'14px 16px' }}>
              <div style={{ fontSize:'13px', fontWeight:500, color:V.rose, fontFamily:"'Jost',sans-serif", marginBottom:'6px' }}>Death Cross</div>
              <p style={{ ...body, margin:0, fontSize:'13px' }}>Faster MA crosses below slower MA. Signals bearish momentum. Reduce put selling, consider other strategies.</p>
            </div>
          </div>
          {infoBox('MAs fanning upward in order (5 above 20 above 50) = strong bullish momentum.\nMAs converging and tangling = momentum fading, trend weakening. Reduce size.\nPrice below all three MAs with MAs crossing down = avoid selling puts on this stock.')}
        </>
      )}
      {sectionBlock('MACD',
        <>
          <p style={body}>MACD measures the relationship between two exponential moving averages (standard: 12, 26, 9). The MACD line is the difference between the 12-period and 26-period EMA. The signal line is a 9-period EMA of MACD. The histogram shows the difference between MACD and signal — growing histogram means momentum building, shrinking means fading.</p>
          {infoBox('KEY: MACD crossing above signal line = bullish. Crossing below = bearish. MACD diverging from price = potential reversal. Histogram shrinking = trend losing momentum.\n\nIMPORTANT: MACD is a lagging indicator. Use it to confirm what RSI and moving averages are already suggesting — never use it alone.')}
        </>
      )}
      {sectionBlock('Volume as confirmation',
        <>{infoBox('Price rising + volume rising = strong bullish momentum. Buyers committed.\nPrice falling + volume rising = strong bearish momentum. Sellers committed.\nPrice rising + volume falling = weak momentum. Rally may be running out of buyers.\nPrice falling + volume falling = selling exhausting. Potential bounce ahead.\n\nFor options sellers volume confirmation matters most at key supply/demand zones and high OI strikes. A break on high volume is meaningful. The same break on low volume is often a false move.')}</>
      )}
      <div style={{ marginBottom:'16px' }}>
        <div style={{ fontSize:'11px', color:V.muted, fontFamily:"'Jost',sans-serif", marginBottom:'8px', letterSpacing:'0.08em' }}>CHART — Price with MAs, RSI, and Volume panels</div>
        <MomentumCanvas />
        <p style={{ fontSize:'12px', color:V.muted, fontFamily:"'Jost',sans-serif", marginTop:'8px', lineHeight:1.6, fontStyle:'italic' }}>Blue = 5MA, Green = 20MA, Red = 50MA. Purple = RSI with 70/30 bands. Green/red bars = volume. Notice the golden cross, bearish divergence annotation, and volume confirmation.</p>
      </div>
    </div>
  );

  const renderTab3 = () => {
    const currentQ = ATQ[atPhase];
    return (
      <div>
        <h2 style={{ ...serif, fontSize:'28px', fontWeight:400, color:V.gold, margin:'0 0 4px' }}>Putting It All Together</h2>
        <p style={{ fontSize:'16px', color:V.muted, fontStyle:'italic', fontFamily:"'Jost',sans-serif", margin:'0 0 16px' }}>Three lenses reading the same market simultaneously.</p>
        {quote('The goal is not to be right about the market. The goal is to read the market correctly and act accordingly.', 'Mark Douglas')}
        <p style={body}>You have learned to read supply and demand zones, open interest levels, and price momentum signals. Now watch how they work together to paint a complete picture — the same way an experienced trader sees a chart.</p>

        {/* Phase labels */}
        {atPhase >= 0 && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'8px', marginBottom:'16px' }}>
            {PHASE_LABELS.map((pl,i) => (
              <div key={i} style={{ background: i <= atPhase ? 'rgba(201,168,76,0.08)' : V.bg2, border:`0.5px solid ${i <= atPhase ? 'rgba(201,168,76,0.3)' : V.border}`, borderRadius:'8px', padding:'8px 10px' }}>
                <div style={{ fontSize:'9px', color: i <= atPhase ? V.gold : V.dim, letterSpacing:'0.1em', textTransform:'uppercase', fontFamily:"'Jost',sans-serif", marginBottom:'3px' }}>Day {pl.day}</div>
                <div style={{ fontSize:'11px', color: i <= atPhase ? V.text : V.muted, fontFamily:"'Jost',sans-serif", fontWeight:500 }}>{pl.title}</div>
              </div>
            ))}
          </div>
        )}

        <AllTogetherCanvas phase={atPhase} />

        {atPhase < 0 && (
          <div style={{ textAlign:'center', marginTop:'20px' }}>
            <button onClick={() => setAtPhase(0)} style={{ padding:'12px 28px', background:'rgba(201,168,76,0.12)', border:'0.5px solid rgba(201,168,76,0.5)', borderRadius:'8px', color:V.goldL, fontSize:'14px', cursor:'pointer', fontFamily:"'Jost',sans-serif", fontWeight:500 }}>
              ▶ Start the Interactive Analysis
            </button>
          </div>
        )}

        {atPhase >= 0 && !atDone && (
          <div style={{ marginTop:'20px' }}>
            {currentQ && (
              <div style={{ background:V.bg2, border:`0.5px solid ${V.border}`, borderRadius:'10px', padding:'20px 22px', marginBottom:'14px' }}>
                <div style={{ fontSize:'10px', letterSpacing:'0.12em', color:V.gold, textTransform:'uppercase', fontFamily:"'Jost',sans-serif", marginBottom:'8px' }}>{PHASE_LABELS[atPhase]?.note}</div>
                <p style={{ fontSize:'14px', fontWeight:500, color:V.text, fontFamily:"'Jost',sans-serif", lineHeight:1.65, marginBottom:'16px' }}>{currentQ.question}</p>
                <div style={{ display:'flex', flexDirection:'column', gap:'8px', marginBottom:'16px' }}>
                  {currentQ.options.map((opt, oi) => {
                    let bg = 'transparent', border = V.border, color = V.text;
                    if (atAnswer !== null) {
                      if (oi === currentQ.correct) { bg='rgba(122,171,138,0.1)'; border='rgba(122,171,138,0.4)'; color=V.sage; }
                      else if (oi === atAnswer) { bg='rgba(196,122,122,0.1)'; border='rgba(196,122,122,0.35)'; color=V.rose; }
                      else color=V.muted;
                    } else if (false) { bg='rgba(201,168,76,0.08)'; }
                    return (
                      <div key={oi} onClick={() => pickATAnswer(oi)} style={{ padding:'10px 14px', borderRadius:'8px', border:`0.5px solid ${border}`, background:bg, color, fontSize:'13px', cursor:atAnswer!==null?'default':'pointer', lineHeight:1.5, display:'flex', gap:'8px', alignItems:'center', fontFamily:"'Jost',sans-serif" }}>
                        {atAnswer!==null&&oi===currentQ.correct&&<span style={{color:V.sage,flexShrink:0}}>✓</span>}
                        {atAnswer!==null&&oi===atAnswer&&oi!==currentQ.correct&&<span style={{color:V.rose,flexShrink:0}}>✗</span>}
                        {opt}
                      </div>
                    );
                  })}
                </div>
                {atAnswer !== null && (
                  <>
                    <div style={{ background:'rgba(201,168,76,0.06)', borderLeft:`3px solid ${V.gold}`, borderRadius:'6px', padding:'12px 16px', marginBottom:'14px' }}>
                      <p style={{ fontSize:'13px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.75, margin:0 }}>{currentQ.explanation}</p>
                    </div>
                    <button onClick={handleATNext} style={{ padding:'10px 22px', background:'rgba(201,168,76,0.12)', border:'0.5px solid rgba(201,168,76,0.5)', borderRadius:'8px', color:V.goldL, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}>
                      {atPhase < 3 ? 'Continue to next phase →' : 'See the full picture →'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {atDone && (
          <div style={{ marginTop:'24px' }}>
            <div style={{ background:'rgba(201,168,76,0.04)', border:`0.5px solid rgba(201,168,76,0.15)`, borderRadius:'12px', padding:'28px 32px', marginBottom:'24px' }}>
              <h3 style={{ ...serif, fontSize:'24px', fontWeight:400, color:V.gold, margin:'0 0 20px' }}>What You Just Read</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'16px', marginBottom:'24px' }}>
                {[
                  ['Supply & Demand', 'Identified two key zones before price arrived at them. Supply at $185 correctly predicted resistance. Demand at $165 correctly predicted support.'],
                  ['Open Interest', 'High call OI at $185 warned of seller positioning at resistance. High put OI at $165 confirmed buyer support. Both proved accurate.'],
                  ['Momentum', 'RSI divergence warned of the rejection before it happened. RSI oversold at the bounce confirmed the entry. Volume confirmed both the rejection and the bounce.'],
                ].map(([t,d]) => (
                  <div key={t} style={{ background:V.bg3, borderRadius:'9px', padding:'16px' }}>
                    <div style={{ fontSize:'12px', fontWeight:500, color:V.gold, fontFamily:"'Jost',sans-serif", marginBottom:'8px' }}>{t}</div>
                    <p style={{ fontSize:'13px', color:V.muted, fontFamily:"'Jost',sans-serif", lineHeight:1.7, margin:0 }}>{d}</p>
                  </div>
                ))}
              </div>
              <p style={{ ...serif, fontSize:'18px', fontStyle:'italic', color:V.gold, textAlign:'center', lineHeight:1.5, margin:'0 0 12px' }}>Three tools. Three confirmations. One high-quality trade decision.</p>
              <p style={{ fontSize:'13px', color:V.muted, fontFamily:"'Jost',sans-serif", textAlign:'center', lineHeight:1.75, maxWidth:'620px', margin:'0 auto' }}>
                This is how experienced traders read charts — not by predicting the future, but by reading multiple sources of evidence until they align. When supply and demand, open interest, and momentum all point to the same conclusion — that is when you act with conviction.
              </p>
            </div>

            {/* Quiz */}
            <h3 style={{ ...serif, fontSize:'24px', fontWeight:400, color:V.gold, marginBottom:'6px' }}>Knowledge Check</h3>
            <p style={{ fontSize:'13px', color:V.muted, fontFamily:"'Jost',sans-serif", marginBottom:'20px' }}>Score 4 out of 5 to pass. Results saved automatically.</p>
            {quizScore === null ? (
              <div>
                <div style={{ display:'flex', gap:'6px', alignItems:'center', marginBottom:'16px' }}>
                  {ADV_QUIZ.map((_,i) => (
                    <div key={i} style={{ width:'8px', height:'8px', borderRadius:'50%', background: i < quizQ ? V.sage : i === quizQ ? V.gold : V.border }} />
                  ))}
                  <span style={{ fontSize:'11px', color:V.muted, fontFamily:"'Jost',sans-serif", marginLeft:'8px' }}>Question {quizQ+1} of {ADV_QUIZ.length}</span>
                </div>
                {(() => {
                  const q = ADV_QUIZ[quizQ];
                  const picked = quizAnswers[quizQ];
                  return (
                    <div style={{ background:V.bg2, border:`0.5px solid ${V.border}`, borderRadius:'10px', padding:'20px 22px' }}>
                      <p style={{ fontSize:'14px', fontWeight:500, color:V.text, fontFamily:"'Jost',sans-serif", lineHeight:1.65, marginBottom:'16px' }}>
                        <span style={{ color:V.gold, marginRight:'8px' }}>{quizQ+1}.</span>{q.q}
                      </p>
                      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                        {q.options.map((opt,oi) => {
                          let bg='transparent', border=V.border, color=V.text;
                          if (quizFeed) {
                            if (oi===q.correct) {bg='rgba(122,171,138,0.1)';border='rgba(122,171,138,0.4)';color=V.sage;}
                            else if (oi===picked) {bg='rgba(196,122,122,0.1)';border='rgba(196,122,122,0.35)';color=V.rose;}
                            else color=V.muted;
                          } else if (picked===oi) {bg='rgba(201,168,76,0.08)';border='rgba(201,168,76,0.4)';color=V.goldL;}
                          return (
                            <div key={oi} onClick={() => pickQuizAnswer(oi)} style={{ padding:'10px 14px', borderRadius:'8px', border:`0.5px solid ${border}`, background:bg, color, fontSize:'13px', cursor:quizFeed?'default':'pointer', lineHeight:1.5, display:'flex', gap:'8px', alignItems:'center', fontFamily:"'Jost',sans-serif" }}>
                              {quizFeed&&oi===q.correct&&<span style={{color:V.sage,flexShrink:0}}>✓</span>}
                              {quizFeed&&oi===picked&&oi!==q.correct&&<span style={{color:V.rose,flexShrink:0}}>✗</span>}
                              {opt}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div style={{ textAlign:'center', padding:'32px 24px', background:V.bg2, borderRadius:'12px', border:`0.5px solid ${quizScore >= 80 ? 'rgba(122,171,138,0.3)' : 'rgba(196,122,122,0.3)'}` }}>
                <div style={{ fontSize:'52px', color: quizScore >= 80 ? V.sage : V.rose, ...serif, marginBottom:'8px' }}>{quizScore}%</div>
                <p style={{ fontSize:'14px', color: quizScore >= 80 ? V.sage : V.rose, marginBottom:'16px', fontFamily:"'Jost',sans-serif" }}>
                  {quizScore >= 80 ? 'You can read the market. Now apply it.' : 'Review the lesson and try again. These concepts take time to internalize.'}
                </p>
                {quizScore >= 80
                  ? <button onClick={() => onNavigate && onNavigate('icharts')} style={{ padding:'11px 24px', background:'rgba(201,168,76,0.12)', border:'0.5px solid rgba(201,168,76,0.5)', borderRadius:'8px', color:V.goldL, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}>Return to Strategy Charts →</button>
                  : <button onClick={() => { setQuizQ(0); setQuizAnswers({}); setQuizScore(null); setTab(0); }} style={{ padding:'11px 24px', background:'transparent', border:`0.5px solid ${V.border}`, borderRadius:'8px', color:V.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif" }}>← Review the lesson</button>
                }
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding:'40px 32px', maxWidth:'860px', background:V.bg, minHeight:'100vh' }}>
      <button style={{ background:'transparent', border:'none', color:V.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif", padding:'0 0 20px', display:'block' }} onClick={onBack}>← Back</button>
      <div style={{ fontSize:'11px', letterSpacing:'0.15em', color:V.gold, fontFamily:"'Jost',sans-serif", fontWeight:600, marginBottom:'6px', textTransform:'uppercase' }}>Tier 2 — Advanced Concepts</div>
      <h1 style={{ ...serif, fontSize:'36px', fontWeight:400, color:V.gold, margin:'0 0 8px', lineHeight:1.2 }}>Reading the Market</h1>
      <p style={{ ...serif, fontSize:'16px', fontStyle:'italic', color:V.muted, margin:'0 0 28px' }}>Three lenses. One complete picture.</p>
      {quote('The tape tells the truth — if you know how to read it.', 'Jesse Livermore')}
      <p style={{ ...body, maxWidth:'720px', marginBottom:'28px' }}>
        Every price move in the market has a reason. Supply and demand created it. Open interest shows where the big money positioned for it. Momentum tells you whether it has conviction behind it or is running out of steam. Learning to read all three simultaneously is what separates traders who react to price from traders who anticipate it.
      </p>
      {/* Tab nav */}
      <div style={{ display:'flex', gap:0, marginBottom:'28px', borderBottom:`0.5px solid ${V.border}` }}>
        {TABS.map((t,i) => (
          <button key={t} onClick={() => setTab(i)} style={{ padding:'10px 22px', background:'transparent', border:'none', borderBottom: tab===i ? `2px solid ${V.gold}` : '2px solid transparent', color: tab===i ? V.gold : V.muted, fontSize:'13px', cursor:'pointer', fontFamily:"'Jost',sans-serif", whiteSpace:'nowrap' }}>{t}</button>
        ))}
      </div>
      {tab === 0 && renderTab0()}
      {tab === 1 && renderTab1()}
      {tab === 2 && renderTab2()}
      {tab === 3 && renderTab3()}
    </div>
  );
}

export default ReadingTheMarketView
